import AccountManager from './accountManager.js';
import Dashboard from './dashboard.js';
import d from './DOM.js';
import network from './network.js';

/**
 * /level — the trader skill badge as its own page (it used to be a small widget in the dashboard
 * header): the level and score, progress to the next level, the weighted score breakdown, and
 * "Share My Stats", which mints the same public badge link as before.
 */

/** Level thresholds — must match the skill-level cut-offs in apps/web/src/tv/pages/dashboard-service.ts. */
const LEVELS = [
	{ name: 'Beginner', min: 0 },
	{ name: 'Developing', min: 40 },
	{ name: 'Competent', min: 65 },
	{ name: 'Skilled', min: 80 },
	{ name: 'Advanced', min: 92 },
];

export default abstract class Level {
	public static init() {
		const startInput = d.qs<HTMLInputElement>('#level-start-date');
		const endInput = d.qs<HTMLInputElement>('#level-end-date');
		const fmt = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
		const today = new Date();
		startInput.value = fmt(Dashboard.monthsBefore(today, 2));
		endInput.value = fmt(today);

		d.qs<HTMLButtonElement>('#level-apply-date').addEventListener('click', () => Level.load());
		Level.initShare();
		Level.load();
	}

	private static range() {
		return {
			startDate: d.qs<HTMLInputElement>('#level-start-date').value,
			endDate: d.qs<HTMLInputElement>('#level-end-date').value,
		};
	}

	private static async load() {
		if (!AccountManager.currentAccount) {
			d.qs<HTMLElement>('#badge-level').textContent = 'No account';
			return;
		}
		const { startDate, endDate } = Level.range();
		const query = `?accountNumber=${encodeURIComponent(AccountManager.currentAccount.accountNumber)}&startDate=${startDate}&endDate=${endDate}`;

		try {
			const res = await fetch(`/api/dashboard/metrics${query}`);
			const data = await res.json();
			if (data.error) { console.error(data.error); return; }
			Level.render(data);
		} catch (err) {
			console.error('Failed to load level', err);
		}
	}

	private static render(data: any) {
		const { skill, overall } = data;
		const score = Math.round(skill.score);
		const fmtNum = (n: number, dp = 2) => new Intl.NumberFormat('en-US', { maximumFractionDigits: dp }).format(n);

		// Badge
		d.qs<HTMLElement>('#badge-level').textContent = skill.level;
		d.qs<HTMLElement>('#badge-score').textContent = `Skill Score: ${score}/100`;
		const icon = d.qs<HTMLElement>('#badge-icon');
		icon.textContent = String(score);
		icon.style.background = Dashboard.badgeGradient(skill.level);

		// Progress to next level
		const idx = Math.max(0, LEVELS.findIndex(l => l.name === skill.level));
		const next = LEVELS[idx + 1];
		const floor = LEVELS[idx].min;
		const fill = d.qs<HTMLElement>('#level-progress-fill');
		if (next) {
			fill.style.width = `${Math.min(100, Math.max(0, ((skill.score - floor) / (next.min - floor)) * 100))}%`;
			d.qs<HTMLElement>('#level-next-label').textContent = `Progress to ${next.name}`;
			d.qs<HTMLElement>('#level-next-points').textContent = `${fmtNum(Math.max(0, next.min - skill.score), 1)} points to go`;
		} else {
			fill.style.width = '100%';
			d.qs<HTMLElement>('#level-next-label').textContent = 'Top level reached';
			d.qs<HTMLElement>('#level-next-points').textContent = '';
		}
		fill.style.background = Dashboard.badgeGradient(skill.level);

		d.qs<HTMLElement>('#level-ladder').innerHTML = LEVELS.map((l, i) => `
			<div class="level-rung ${i === idx ? 'current' : ''} ${i < idx ? 'passed' : ''}">
				<span class="level-rung-dot" style="background: ${i <= idx ? Dashboard.badgeGradient(l.name) : 'var(--theme-bg-input)'}"></span>
				<span class="level-rung-name">${l.name}</span>
				<span class="level-rung-min">${l.min}+</span>
			</div>
		`).join('');

		// Weighted components
		const components: { key: string; label: string; score: number; weight: number }[] = skill.components || [];
		d.qs<HTMLElement>('#level-components').innerHTML = components.map(c => {
			const pts = c.score * c.weight;
			const max = c.weight * 100;
			return `
				<div class="level-component">
					<div class="fr spaceBetween" style="gap: 10px;">
						<span><strong>${c.label}</strong> <span class="level-weight">${Math.round(c.weight * 100)}%</span></span>
						<span class="level-points">${fmtNum(pts, 1)} / ${fmtNum(max, 0)} pts</span>
					</div>
					<div class="level-bar"><div style="width: ${Math.min(100, Math.max(0, c.score))}%;"></div></div>
				</div>
			`;
		}).join('');

		d.qs<HTMLElement>('#level-pf').textContent = fmtNum(overall.profitFactor);
		d.qs<HTMLElement>('#level-winrate').textContent = `${fmtNum(overall.winRate, 1)}%`;
		d.qs<HTMLElement>('#level-trades').textContent = String(overall.totalTrades);
		d.qs<HTMLElement>('#level-months').textContent = `${fmtNum(skill.monthsActive, 1)} Mo`;
	}

	/** "Share My Stats" — unchanged behaviour: mint a badge link for the range, copy it. */
	private static initShare() {
		const shareBtn = d.qs<HTMLButtonElement>('#btn-share-badge');
		const preview = d.qs<HTMLAnchorElement>('#btn-preview-badge');
		const linkRow = d.qs<HTMLElement>('#share-link-row');
		const linkInput = d.qs<HTMLInputElement>('#share-link-input');

		linkInput.addEventListener('focus', () => linkInput.select());

		shareBtn.addEventListener('click', async () => {
			const originalText = shareBtn.innerHTML;
			shareBtn.innerHTML = 'Generating...';
			const restore = () => setTimeout(() => { shareBtn.innerHTML = originalText; }, 2000);

			try {
				const accountNumber = AccountManager.currentAccount?.accountNumber;
				const { startDate, endDate } = Level.range();
				const result = await network.ap('/api/share/link', { accountNumber, startDate, endDate });

				if (result.ok && result.payload.url) {
					const url: string = result.payload.url;
					linkInput.value = url;
					linkRow.classList.remove('hidden');
					preview.href = url;
					preview.classList.remove('hidden');
					try {
						await navigator.clipboard.writeText(url);
						shareBtn.innerHTML = 'Copied!';
					} catch {
						// Clipboard blocked (permissions / insecure context): the link is shown to copy by hand.
						shareBtn.innerHTML = 'Link ready';
						linkInput.focus();
					}
				} else {
					shareBtn.innerHTML = 'Error';
				}
			} catch (e) {
				console.error(e);
				shareBtn.innerHTML = 'Error';
			}
			restore();
		});
	}
}
